import React, { useContext, useMemo } from 'react';
import { encode } from 'html-entities';
import { WixRicosViewerNext } from '@wix/ricos-viewer';
import {
  ensureRicosContent,
  ensureDraftContent,
} from 'ricos-content/libs/converters';
import { fromDraft, toDraft } from 'ricos-content/libs/migrateSchema';

import { pluginLink } from 'wix-rich-content-plugin-link/viewer';
import { pluginImage } from 'wix-rich-content-plugin-image/viewer';
import { pluginVideo } from 'wix-rich-content-plugin-video/viewer';
import { pluginIndent } from 'wix-rich-content-plugin-indent/viewer';
import { pluginGiphy } from 'wix-rich-content-plugin-giphy/viewer';
import { pluginMap } from 'wix-rich-content-plugin-map/viewer';
import { pluginLineSpacing } from 'wix-rich-content-plugin-line-spacing/viewer';
import { pluginLinkPreview } from 'wix-rich-content-plugin-link-preview/viewer';
import { pluginEmoji } from 'wix-rich-content-plugin-emoji/viewer';
import { pluginFileUpload } from 'wix-rich-content-plugin-file-upload/viewer';
import { pluginVerticalEmbed } from 'wix-rich-content-plugin-vertical-embed/viewer';
import { pluginDivider } from 'wix-rich-content-plugin-divider/viewer';
import { pluginHtml } from 'wix-rich-content-plugin-html/viewer';
import { pluginAudio } from 'wix-rich-content-plugin-audio/viewer';
import { pluginGallery } from 'wix-rich-content-plugin-gallery/viewer';
import { pluginLinkButton } from 'wix-rich-content-plugin-button/viewer';
import { pluginTable } from 'wix-rich-content-plugin-table/viewer';
import { pluginTextColor } from 'wix-rich-content-plugin-text-color/viewer';
import { DraftJSObject } from '../../../../types/RichContent';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { AppContext } from '../SettingsProvider/SettingsProvider';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import settingsParams from '../../settingsParams';
import { st, classes } from '../QuestionsWrapper/styles.st.css';
import stylesParams from '../../stylesParams';
import { useLayoutType } from '../../../../hooks/useLayoutType';

enum PlatformType {
  Livesite = 'Livesite',
  Editor = 'Editor',
}

const RCV = ({
  convertAnswerToJSON,
}: {
  convertAnswerToJSON: DraftJSObject | string;
}) => {
  const { instance, isEditor3, questionId } = useContext(AppContext);
  const environment = useEnvironment();
  const settings = useSettings();
  const styles = useStyles();
  const answerFont = styles.get(stylesParams.answerTextFont);

  const customStyles = useMemo(() => {
    const elements = [
      'p',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'span',
      'link',
      'hashtag',
      'quote',
    ];
    const customAnswerStyle = {
      fontFamily: answerFont.family,
      fontSize: `${answerFont.size}px`,

      ...(answerFont.style?.bold && { fontWeight: 'bold' }),
      ...(answerFont.style?.italic && { fontStyle: 'italic' }),
      ...(answerFont.style?.underline && { textDecoration: 'underline' }),
    };

    const result = elements.reduce((acc: { [key: string]: any }, element) => {
      acc[element] = customAnswerStyle;
      return acc;
    }, {});
    return result;
  }, [answerFont]);
  const {
    layout: { layoutValue: layout },
  } = useLayoutType(isEditor3);
  const validContent =
    typeof convertAnswerToJSON === 'string'
      ? encode(convertAnswerToJSON)
      : toDraft(
          fromDraft(ensureDraftContent(convertAnswerToJSON), {
            ignoreUnsupportedValues: true,
          }),
        );
  const textAlignment = settings.get(settingsParams.textAlignment);
  const direction = textAlignment === 'right' ? 'rtl' : 'ltr';
  const parentClass = st(
    classes.answerWrapper,
    { layout },
    questionId ? `RCV${questionId}` : undefined,
  );
  return (
    <div style={{ direction }} className={st(classes.answer, { layout })}>
      {typeof convertAnswerToJSON === 'string' ? (
        convertAnswerToJSON
      ) : (
        <WixRicosViewerNext
          theme={{
            parentClass,
            customStyles: customStyles ?? {},
          }}
          textAlignment={textAlignment}
          cssOverride={{ margin: 0, padding: 0 }}
          instance={instance}
          biSettings={{
            consumer: '[wix-labs] faq-ooi widget',
            usage: layout,
            platform: environment.isViewer
              ? PlatformType.Livesite
              : PlatformType.Editor,
          }}
          plugins={[
            pluginLink(),
            pluginLinkPreview(),
            pluginImage({ disableTransition: true }),
            pluginVideo(),
            pluginAudio({
              getAudioUrl: (src: any) =>
                `https://static.wixstatic.com/${src.id}`,
            }),
            pluginGallery({
              disableExpand: true,
              scrollingElement: () =>
                document.querySelector('[data-hook="expandIcon"]'),
            }),
            pluginMap(),
            pluginDivider(),
            pluginIndent(),
            pluginEmoji(),
            pluginFileUpload(),
            pluginGiphy(),
            pluginLineSpacing(),
            pluginVerticalEmbed(),
            pluginLinkPreview(),
            pluginHtml({
              width: '100%',
              minWidth: '100%',
              siteDomain: 'https://nivina3.wixsite.com/my-site-334',
            }),
            pluginLinkButton(),
            pluginTable(),
            pluginTextColor(),
          ]}
          content={ensureRicosContent(validContent as DraftJSObject)}
        />
      )}
    </div>
  );
};

export default RCV;
